import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { useAuthStore } from '../../auth/store/useAuthStore';
import {
  shouldAutoPullCatalog,
} from '../../sync/core/masterSyncPolicy';
import { SyncPullFailedError } from '../../sync/core/syncPullErrors';
import {
  getActiveCatalogSnapshotSync,
  syncCatalogSnapshotFromRemote,
  type CatalogSnapshotSyncResult,
} from './catalogSnapshotSync';
import { isSellableCatalogEmpty } from './catalogSnapshotFlags';
import {
  getCatalogRamSnapshotCache,
  type CatalogRamSnapshotCache,
} from './catalogRamSnapshotCache';
import { shouldPropagateCatalogReadError } from './catalogReadPreflightPolicy';
import type { CatalogReadPreflightResult } from '../domain/catalogTypes';
export { isLocalCatalogEmpty, isSellableCatalogEmpty } from './catalogSnapshotFlags';
export { shouldSkipRemoteSnapshotDueToStockQueue } from './catalogReadPreflightPolicy';

let catalogReadPreflightInFlight: Promise<CatalogReadPreflightResult> | null = null;
let catalogReadPreflightInFlightForced = false;

export function resetCatalogReadPreflightState(): void {
  catalogReadPreflightInFlight = null;
  catalogReadPreflightInFlightForced = false;
}

/**
 * Dependencias inyectables para tests del preflight (mismo patrón que
 * `hasSession` en `syncCatalogSnapshotFromRemote`). En producción no se pasan.
 */
export type CatalogReadPreflightDeps = {
  hasSession?: () => boolean;
  isOnline?: () => Promise<boolean>;
  getCache?: () => CatalogRamSnapshotCache | null;
  syncNow?: (opts?: {
    force?: boolean;
    forceFull?: boolean;
  }) => Promise<CatalogSnapshotSyncResult>;
};

/**
 * Si hay sesión y red utilizable, hidrata la cache RAM con el snapshot remoto antes de leer productos/stock.
 * Si no hay sesión: no-op. Si hay datos cacheados y falla el pull: no-op (se sirve la cache).
 * Si la cache está vacía y el pull remoto falla (o no hay red): PROPAGA el error —
 * un fallo con pantalla sin datos es error + Reintentar, nunca "lista vacía" (falso empty).
 * El criterio de “cuándo” llamar a esta función (p. ej. al cambiar de sucursal) lo definen las pantallas con refs.
 *
 * Singleflight force-aware: una corrida forzada cubre a las no-forzadas, pero una
 * llamada forzada NUNCA se une a una corrida no-forzada en vuelo — ésta puede
 * terminar sin datos (cooldown / error tragado) y dejaría el refresh sin pull.
 */
export async function preflightCatalogSnapshotForRead(opts?: {
  force?: boolean;
  deps?: CatalogReadPreflightDeps;
}): Promise<CatalogReadPreflightResult> {
  const force = opts?.force === true;
  if (
    catalogReadPreflightInFlight &&
    (catalogReadPreflightInFlightForced || !force)
  ) {
    return catalogReadPreflightInFlight;
  }

  const deps = opts?.deps;

  const run = (async () => {
    const hasSession =
      deps?.hasSession ?? (() => !!useAuthStore.getState().session);
    if (!hasSession()) {
      return {};
    }

    const getCache = deps?.getCache ?? getCatalogRamSnapshotCache;
    const webSnapshot = getCache();
    // ADR-017 Fase 4: ramOnly — el "local" es la cache RAM.
    const localEmpty = !webSnapshot || webSnapshot.products.length === 0;

    const effectiveForce = force || localEmpty;

    let online = false;
    try {
      online = await (deps?.isOnline ?? isOnline)();
    } catch {
      online = false;
    }
    if (!online) {
      // Sin red con la pantalla sin datos = error visible, no falso empty.
      if (effectiveForce) {
        throw new SyncPullFailedError(
          'catalog',
          'Sin conexión. No se pudo actualizar catálogo e inventario.'
        );
      }
      return {};
    }

    try {
      if (!effectiveForce) {
        const inFlight = getActiveCatalogSnapshotSync();
        if (inFlight) {
          const syncResult = await inFlight;
          return await buildCatalogStillEmptyResult(syncResult, getCache);
        }
        if (!(await shouldAutoPullCatalog({ localProductsCount: null }))) {
          return {};
        }
      }
      // force = bypass cooldown; forceFull solo vacío local o pull-to-refresh manual.
      // Con effectiveForce NO se coalesce a un pull no-forzado en vuelo: éste puede
      // resolver applied:false tragando el error remoto → falso empty. La coalescing
      // correcta la hace syncCatalogSnapshotFromRemote (forceFull solo se une a forced).
      const syncResult = await (deps?.syncNow ?? syncCatalogSnapshotFromRemote)({
        force: effectiveForce,
        forceFull: force || localEmpty,
      });
      return await buildCatalogStillEmptyResult(syncResult, getCache);
    } catch (e) {
      if (shouldPropagateCatalogReadError({ effectiveForce, error: e })) {
        throw e;
      }
      const msg = e instanceof Error ? e.message : String(e);
      console.warn('[catalogReadPreflight] syncCatalogSnapshotFromRemote', msg);
      return {};
    }
  })();

  catalogReadPreflightInFlightForced = force;
  // `run.finally()` devuelve una promesa NUEVA: hay que comparar contra esa
  // promesa trackeada, no contra `run`, o el flag nunca se limpia y todas las
  // llamadas posteriores devuelven el resultado viejo (stale-post-recovery).
  const tracked = run.finally(() => {
    if (catalogReadPreflightInFlight === tracked) {
      catalogReadPreflightInFlight = null;
      catalogReadPreflightInFlightForced = false;
    }
  });
  catalogReadPreflightInFlight = tracked;
  return catalogReadPreflightInFlight;
}

async function buildCatalogStillEmptyResult(
  syncResult?: CatalogSnapshotSyncResult,
  getCache: () => CatalogRamSnapshotCache | null = getCatalogRamSnapshotCache
): Promise<CatalogReadPreflightResult> {
  if (syncResult?.catalogEmpty === true) {
    return { catalogStillEmpty: true };
  }
  if (syncResult?.catalogEmpty === false) {
    return {};
  }

  try {
    const cached = getCache();
    const sellableCount = (cached?.products ?? []).filter((p) => p.is_misc !== true).length;
    if (isSellableCatalogEmpty({ sellableProductsCount: sellableCount })) {
      return { catalogStillEmpty: true };
    }
    return {};
  } catch {
    return {};
  }
}
